/**
 * Shared error handling utilities
 */

// API error type guard
interface ApiError {
  response?: {
    data?: {
      error?: string;
      message?: string;
    };
    status?: number;
  };
}

function isApiError(error: unknown): error is ApiError {
  return typeof error === "object" && error !== null && "response" in error;
}

/** True only for a well-formed 4xx HTTP status. Anything else — 5xx, missing,
 * NaN, or otherwise non-numeric — is treated as "not safe to show `message`". */
function isClientErrorStatus(status: unknown): boolean {
  return typeof status === "number" && Number.isFinite(status) && status >= 400 && status < 500;
}

/**
 * Extract error message from API error or return fallback.
 *
 * Prefers `data.message` over `data.error`, but ONLY for 4xx responses. In
 * Fastify/AJV's own 400 shape (e.g. `{ error: "Bad Request", message:
 * "body/campaignId must NOT have fewer than 1 characters" }`), `error` is the
 * generic category while `message` is the specific, human-readable detail
 * meant for the user — so on a 4xx it's safe (and useful) to prefer it.
 *
 * This backend registers no `setErrorHandler` anywhere, so any *unhandled*
 * 500 falls through to Fastify's default error body:
 * `{ statusCode: 500, error: "Internal Server Error", message: <raw
 * err.message> }`. That raw `err.message` is routinely a Prisma invocation
 * dump, a Postgres connection string, or other internal/PII-bearing detail —
 * never something meant for a user-facing toast. So for 5xx responses (and
 * for any response whose status isn't a readable 4xx) we deliberately fall
 * back to `data.error`, then `fallback`, and never surface `data.message`.
 *
 * Responses that only ever set `error` are unaffected by this split and
 * behave exactly as before.
 */
export function getErrorMessage(error: unknown, fallback: string): string {
  if (isApiError(error)) {
    const data = error.response?.data;
    if (isClientErrorStatus(error.response?.status) && data?.message) {
      return data.message;
    }
    return data?.error || fallback;
  }
  return fallback;
}
